import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import type { UserMessage } from "../utils/errorMessages.js";

/**
 * <cos-error-state> — the single error UI used by every async surface.
 *
 * Pink banner with mapped friendly copy + an explicit, styled action
 * button. Replaces the previous ad-hoc `<div role="alert">` blocks
 * sprinkled through components, all of which leaked raw error strings
 * to end users.
 *
 * Action button mapping:
 *   cta="retry"    → "Retry"        (fires cos:error:retry)
 *   cta="sign-in"  → "Sign in again"(fires cos:error:sign-in)
 *   cta="go-back"  → "Go back"      (fires cos:error:go-back)
 *   cta omitted    → no button
 *
 * Hosting components decide which event to listen for. The component
 * itself is dumb — it just renders the banner + emits an event when
 * the user clicks. Centralising the visual treatment here means
 * future contrast / icon / animation tweaks land in one place.
 *
 * Always render this WITHIN page chrome. Never bare-render at the
 * route level.
 *
 * `cos:error:shown` — a SEPARATE, general-purpose signal from the three
 * click-driven events above: it fires whenever this element renders a
 * (new) error, not when the user acts on it. Because this is THE single
 * error UI every async surface uses, that gives any listener a universal
 * "a surface on this page just failed, and here's its HTTP status if one's
 * known" hook without threading a bespoke callback through every page.
 * First consumer: the re-test-link fallback offer in cos-feedback-panel.ts
 * (a report's stored destination page routes fine but the RECORD it
 * pointed at is gone — a page-level error state is the only client-side
 * signal that ever surfaces that). Fires once per genuinely NEW error
 * (compared by rendered content, not by prop-object identity — a parent
 * that re-renders `.message=${userMessage(this._error)}` on every render
 * hands this component a fresh object with IDENTICAL content each time,
 * and that must not re-fire) — see _resolvedKey.
 */
@customElement("cos-error-state")
export class CosErrorState extends LitElement {
	@property({ type: Object }) message?: UserMessage;
	// Convenience props so tests + simple consumers can set the fields
	// individually without constructing a UserMessage object.
	@property({ type: String }) heading = "";
	@property({ type: String }) description = "";
	@property({ type: Boolean }) canRetry = false;
	@property({ type: String }) cta: "retry" | "sign-in" | "go-back" | "" = "";

	static styles = css`
		:host {
			display: block;
		}
		.banner {
			background: var(--cos-status-danger-wash);
			color: var(--cos-status-danger-text);
			border: 1px solid var(--cos-status-danger-wash);
			border-radius: var(--cos-radius-sm);
			padding: var(--cos-space-4) var(--cos-space-5);
			display: flex;
			gap: var(--cos-space-4);
			align-items: flex-start;
		}
		.icon {
			flex: 0 0 24px;
			width: 24px;
			height: 24px;
			margin-top: 2px;
			color: var(--cos-status-danger-text);
		}
		.body {
			flex: 1;
			min-width: 0;
		}
		.title {
			font-weight: 600;
			font-size: var(--cos-font-size-base);
			margin: 0 0 var(--cos-space-1) 0;
		}
		.desc {
			font-size: var(--cos-font-size-sm);
			color: var(--cos-status-danger-text);
			margin: 0 0 var(--cos-space-3) 0;
			line-height: 1.5;
		}
		.action {
			display: inline-block;
			padding: var(--cos-space-2) var(--cos-space-4);
			border-radius: var(--cos-radius-sm);
			font-family: var(--cos-font-family);
			font-size: var(--cos-font-size-sm);
			font-weight: 600;
			cursor: pointer;
			border: 1px solid var(--cos-status-danger-line);
			background: var(--cos-status-danger-solid);
			color: var(--cos-text-on-color);
		}
		.action:hover { background: color-mix(in srgb, var(--cos-status-danger-solid) 85%, black); border-color: color-mix(in srgb, var(--cos-status-danger-solid) 85%, black); }
		.action:focus-visible {
			outline: 2px solid var(--cos-status-danger-line);
			outline-offset: 2px;
		}
	`;

	// The last resolved-error "fingerprint" this instance has already
	// announced via cos:error:shown — null until the first render. A
	// VALUE comparison (not `this.message !== prevMessage`, which Lit's own
	// changed-properties tracking would give for free but is reference-
	// based) is what keeps a same-content re-render silent; see the
	// cos:error:shown doc comment above.
	private _lastAnnouncedKey: string | null = null;

	private _resolved(): { title: string; description: string; canRetry: boolean; cta?: string; status?: number } {
		if (this.message) {
			return {
				title: this.message.title,
				description: this.message.description,
				canRetry: this.message.canRetry,
				cta: this.message.cta,
				status: this.message.status,
			};
		}
		return {
			title: this.heading || "Something went wrong",
			description: this.description,
			canRetry: this.canRetry,
			cta: this.cta || (this.canRetry ? "retry" : undefined),
		};
	}

	protected updated(changed: PropertyValues<this>): void {
		super.updated(changed);
		const r = this._resolved();
		const key = JSON.stringify(r);
		if (key === this._lastAnnouncedKey) return;
		this._lastAnnouncedKey = key;
		this.dispatchEvent(new CustomEvent("cos:error:shown", { detail: { status: r.status }, bubbles: true, composed: true }));
	}

	private _onAction() {
		const { cta } = this._resolved();
		const eventName = cta === "sign-in" ? "cos:error:sign-in"
			: cta === "go-back" ? "cos:error:go-back"
			: "cos:error:retry";
		this.dispatchEvent(new CustomEvent(eventName, { bubbles: true, composed: true }));
	}

	private _actionLabel(cta: string | undefined): string {
		switch (cta) {
			case "sign-in": return "Sign in again";
			case "go-back": return "Go back";
			case "retry":   return "Retry";
			default:        return "";
		}
	}

	render() {
		const { title, description, cta } = this._resolved();
		const label = this._actionLabel(cta);
		return html`
			<div class="banner" role="alert" aria-live="assertive">
				<svg class="icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
					<path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
				</svg>
				<div class="body">
					<p class="title">${title}</p>
					${description ? html`<p class="desc">${description}</p>` : nothing}
					${label ? html`<button class="action" type="button" @click=${() => this._onAction()}>${label}</button>` : nothing}
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"cos-error-state": CosErrorState;
	}
}
